import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Stack,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useMFA } from '../../contexts/MFAContext';
import { useAuth } from '../../contexts/AuthContext';
import { MFATypes } from '@flashbacktech/flashbackclient/api';

interface GoogleAuthSetupProps {
  onSetupComplete: () => void;
}

const GoogleAuthSetup: React.FC<GoogleAuthSetupProps> = ({ onSetupComplete }) => {
  const { mfaState, setupMFAMethod, verifyMFASetup, clearError } = useMFA();
  const { authState } = useAuth();
  const [verificationCode, setVerificationCode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Automatically start setup when component mounts
  useEffect(() => {
    if (!mfaState.setupData && !mfaState.isSettingUp) {
      handleSetup();
    }
  }, []);

  const handleSetup = async () => {
    clearError();
    await setupMFAMethod(MFATypes.MFAType.GOOGLE_AUTH);
  };

  const handleVerificationClick = () => {
    if (!verificationCode.trim()) return;
    setShowConfirm(true);
  };

  const handleVerificationConfirmed = async () => {
    setShowConfirm(false);
    if (!verificationCode.trim()) return;

    setIsVerifying(true);
    try {
      await verifyMFASetup(MFATypes.MFAType.GOOGLE_AUTH, verificationCode);

      // Clear the verification code
      setVerificationCode('');

      // Call the completion callback
      onSetupComplete();
    } catch (error) {
      // Error is already handled by verifyMFASetup in the context
    } finally {
      setIsVerifying(false);
    }
  };

  const handleShowBackupCodes = () => {
    setShowBackupCodes(true);
  };

  const handleHideBackupCodes = () => {
    setShowBackupCodes(false);
  };

  const setupData = mfaState.setupData;
  const isSettingUp = mfaState.isSettingUp;
  const error = mfaState.error;

  // Debug logging for QR code data
  if (setupData?.qrCode) {
    // Check if data is too long for QR code (typical limit is around 2,953 bytes for version 40)
    if (setupData.qrCode.length > 2000) {
      console.warn('QR code data is quite long, may not display properly');
    }
  }

  // Generate QR code URL from secret key if we have one
  const generateQRCodeData = () => {
    if (!setupData?.secret) return null;

    // Generate a proper Google Authenticator URL
    const issuer = 'Flashgate';
    const account = authState.user?.email || 'user@flashback.tech';
    const secret = setupData.secret;

    // Create the otpauth URL
    const qrCodeUrl = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(
      account
    )}?secret=${secret}&issuer=${encodeURIComponent(issuer)}&algorithm=SHA1&digits=6&period=30`;

    return qrCodeUrl;
  };

  const qrCodeData = generateQRCodeData();

  if (!setupData && !isSettingUp) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" p={3}>
            <CircularProgress />
            <Typography variant="body2" ml={2}>
              Starting Authenticator setup...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (isSettingUp) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" p={3}>
            <CircularProgress />
            <Typography variant="body2" ml={2}>
              Setting up Google/MS Authenticator...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{ borderColor: '#E9EAEB', borderRadius: '12px', boxShadow: 'none' }}
    >
      <CardContent sx={{ p: 2 }}>
        <Typography
          gutterBottom
          sx={{ fontWeight: 600, fontSize: '16px', lineHeight: '24px', color: '#101828' }}
        >
          Google/MS Authenticator Setup
        </Typography>

        <Grid container spacing={2} alignItems="stretch">
          {/* QR Code */}
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                textAlign: 'center',
                border: '1px solid #E9EAEB',
                borderRadius: '8px',
                boxShadow: 'none',
                bgcolor: 'white',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                gutterBottom
                sx={{ fontWeight: 600, fontSize: '14px', lineHeight: '20px', color: '#414651' }}
              >
                Scan QR Code
              </Typography>
              {qrCodeData && (
                <Box display="flex" justifyContent="center" mb={2}>
                  {qrCodeData.length > 2000 ? (
                    <Box>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        QR Code data is too long to display. Please use manual entry instead.
                      </Alert>
                      <Typography variant="body2" color="textSecondary" sx={{ color: '#717680' }}>
                        The setup data contains {qrCodeData.length} characters, which exceeds the QR
                        code capacity.
                      </Typography>
                    </Box>
                  ) : (
                    <QRCodeSVG value={qrCodeData} size={200} />
                  )}
                </Box>
              )}
              <Typography
                sx={{ fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#717680' }}
              >
                {qrCodeData && qrCodeData.length > 2000
                  ? 'Use the manual entry method below instead of scanning'
                  : 'Open Google/MS Authenticator and scan this QR code'}
              </Typography>
            </Paper>
          </Grid>

          {/* Manual Entry */}
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid #E9EAEB',
                borderRadius: '8px',
                boxShadow: 'none',
                bgcolor: 'white',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                gutterBottom
                sx={{ fontWeight: 600, fontSize: '14px', lineHeight: '20px', color: '#414651' }}
              >
                Manual Entry
              </Typography>
              {setupData?.secret && (
                <TextField
                  fullWidth
                  label="Secret Key"
                  value={setupData.secret}
                  InputProps={{ readOnly: true }}
                  sx={{
                    mt: 2,
                    mb: 1.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      height: '40px',
                    },
                  }}
                  size="small"
                />
              )}
              <Typography
                sx={{ fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#717680' }}
              >
                If you can't scan the QR code, manually enter this secret key in Google
                Authenticator
              </Typography>
            </Paper>
          </Grid>

          {/* Verification */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid #E9EAEB',
                borderRadius: '8px',
                boxShadow: 'none',
                bgcolor: 'white',
              }}
            >
              <Typography
                gutterBottom
                sx={{ fontWeight: 600, fontSize: '14px', lineHeight: '20px', color: '#414651' }}
              >
                Verification
              </Typography>
              <Typography
                paragraph
                sx={{ fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#717680' }}
              >
                Enter the 6-digit code from Google Authenticator to complete setup
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems="center">
                <TextField
                  fullWidth
                  label="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="000000"
                  inputProps={{ maxLength: 6 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' } }}
                  size="small"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleVerificationClick}
                  disabled={!verificationCode.trim() || isVerifying}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    minWidth: 140,
                    height: 40,
                    px: 2,
                    bgcolor: '#617BFF',
                    '&:hover': { bgcolor: '#4A67E3' },
                  }}
                >
                  {isVerifying ? <CircularProgress size={20} /> : 'Verify & Enable'}
                </Button>
              </Stack>
            </Paper>
          </Grid>

          {/* Backup Codes */}
          {setupData?.backupCodes && (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #E9EAEB',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  bgcolor: 'white',
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography
                    sx={{ fontWeight: 600, fontSize: '14px', lineHeight: '20px', color: '#414651' }}
                  >
                    Backup Codes
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleShowBackupCodes}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      borderColor: '#E9EAEB',
                      color: '#414651',
                      '&:hover': { borderColor: '#E9EAEB', bgcolor: 'transparent' },
                    }}
                  >
                    View Backup Codes
                  </Button>
                </Box>
                <Typography
                  sx={{ fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#717680' }}
                >
                  Save these backup codes in a secure location. You can use them to access your
                  account if you lose your authenticator device.
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Confirmation Dialog */}
        <Dialog
          open={showConfirm}
          onClose={() => setShowConfirm(false)}
          maxWidth="xs"
          fullWidth={false}
          PaperProps={{ sx: { width: 400, maxWidth: '90vw', borderRadius: '16px', p: 0, overflow: 'hidden' } }}
        >
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '12px', background: 'linear-gradient(135deg, #617BFF 0%, #8B5CF6 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
            </Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#181D27', mb: 0.5 }}>
              Enable Authenticator?
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#717680', lineHeight: 1.5, mb: 3 }}>
              This will activate Google/MS Authenticator as a two-factor authentication method on your account. You will need to use it to verify your identity when logging in.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
              <Button
                onClick={() => setShowConfirm(false)}
                sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600, fontSize: '13px', color: '#535862', border: '1px solid #E5E7EB', px: 3, py: 1, '&:hover': { backgroundColor: '#F9FAFB' } }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleVerificationConfirmed}
                variant="contained"
                sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600, fontSize: '13px', bgcolor: '#617BFF', px: 3, py: 1, '&:hover': { bgcolor: '#5A6FE8' } }}
              >
                Confirm & Enable
              </Button>
            </Box>
          </Box>
        </Dialog>

        {/* Backup Codes Dialog */}
        <Dialog
          open={showBackupCodes}
          onClose={handleHideBackupCodes}
          maxWidth="xs"
          fullWidth={false}
          PaperProps={{ sx: { width: 360, maxWidth: '90vw', borderRadius: '12px' } }}
        >
          <DialogTitle>Backup Codes</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Save these codes in a secure location. They will only be shown once.
            </Alert>
            <Grid container spacing={1}>
              {setupData?.backupCodes?.map((code: string, index: number) => (
                <Grid item xs={6} sm={4} key={index}>
                  <Paper elevation={1} sx={{ p: 1, textAlign: 'center', fontFamily: 'monospace' }}>
                    {code}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleHideBackupCodes} color="primary">
              I've Saved These Codes
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default GoogleAuthSetup;
